<template>
  <div class="study-creation-container">
    <!-- STEPPER HEADER -->
    <div class="stepper" v-if="steps && steps.length">
      <div v-for="(s, idx) in steps" :key="s.id" class="stepper-item">
        <button
          type="button"
          class="stepper-circle"
          :class="{
            'is-active': s.id === step,
            'is-completed': s.isCompleted,
            'is-error': s.hasError,
            'is-disabled': s.disabled
          }"
          :disabled="s.disabled"
          @click="onStepClick(s.id)"
        >
          <span>{{ s.id }}</span>
        </button>
        <div class="stepper-label">{{ s.label }}</div>

        <div
          v-if="idx < steps.length - 1"
          class="stepper-connector"
          :class="{ 'connector-completed': s.id < step }"
        ></div>
      </div>
    </div>

    <!-- STEP 1 -->
    <div v-if="step === 1" class="new-study-form">
      <div class="step1-header">
        <h2 class="step-title">{{ panelTitle }}</h2>

        <!-- Import options (ONLY for new study creation) -->
        <div v-if="!isEditing" class="import-template-actions">
          <div class="import-action-buttons">
            <button
              type="button"
              class="btn-option"
              :disabled="importingTemplate"
              @click.prevent="triggerTemplatePick"
              title="Import a template-only JSON exported from another device"
            >
              {{ importingTemplate ? "Importing…" : "Import Study Template" }}
            </button>

            <button
              type="button"
              class="btn-option"
              @click.prevent="openStudyDataImport"
              title="Import participant data from a CSV or Excel file"
            >
              Import Study (Data)
            </button>
          </div>

          <input
            ref="templateFileInput"
            class="hidden-file"
            type="file"
            accept=".json,application/json"
            @change="onTemplatePicked"
          />

          <div v-if="templateFileName" class="import-file-name" :title="templateFileName">
            {{ templateFileName }}
          </div>

          <p v-if="templateImportError" class="import-error">{{ templateImportError }}</p>
        </div>
      </div>

      <div v-for="(f, i) in studySchema.filter((f) => f.display)" :key="i" class="schema-field-row">
        <BaseTextarea
          v-if="f.type === 'textarea'"
          v-model="studyData[f.field]"
          :id="f.field"
          :label="f.label"
          :placeholder="f.placeholder"
          :required="f.required"
          :disabled="f.disabled"
          :error="fieldError(f, studyData[f.field])"
        />
        <BaseSelectField
          v-else-if="f.type === 'select'"
          v-model="studyData[f.field]"
          :id="f.field"
          :label="f.label"
          :options="f.options"
          :placeholder="f.placeholder"
          :required="f.required"
          :disabled="f.disabled"
          :error="fieldError(f, studyData[f.field])"
        />
        <BaseNumberField
          v-else-if="f.type === 'number'"
          v-model="studyData[f.field]"
          :id="f.field"
          :label="f.label"
          :placeholder="f.placeholder"
          :required="f.required"
          :disabled="f.disabled"
          :error="fieldError(f, studyData[f.field])"
        />
        <BaseDateField
          v-else-if="f.type === 'date'"
          v-model="studyData[f.field]"
          :id="f.field"
          :label="f.label"
          :required="f.required"
          :disabled="f.disabled"
          :error="fieldError(f, studyData[f.field])"
          :min="f.min"
          :max="f.max"
        />
        <BaseTextField
          v-else
          v-model="studyData[f.field]"
          :id="f.field"
          :label="f.label"
          :placeholder="f.placeholder"
          :required="f.required"
          :disabled="f.disabled"
          :error="fieldError(f, studyData[f.field])"
        />
      </div>

      <div class="form-actions">
          <button type="button" @click="backFromStep1" class="btn-option">Back</button>

          <button type="button" class="btn-option" @click="requestSaveAndContinue" :disabled="unsavedBusy || !saveDirty">
            {{ unsavedBusy ? "Saving…" : (isPublishedStudy ? "Review and Save Changes" : "Save and Continue") }}
          </button>

          <button
            v-if="!isEditing"
            type="button"
            class="btn-option"
            @click="onUnsavedSaveAndExit"
            :disabled="unsavedBusy"
          >
            {{ unsavedBusy ? "Saving…" : "Save Draft and Leave" }}
          </button>

          <button v-if="isEditing && !isPublishedStudy" type="button" class="btn-option" @click="saveThisStepAndBackToStudy">
            {{ isPublishedStudy ? "Review and save changes" : "Save and Leave" }}
          </button>

          <button type="button" @click="validateStudy()" class="btn-option">
            Next
          </button>
        </div>
    </div>

    <!-- STEP 2 -->
    <div v-if="step === 2" class="new-study-form">
      <h2>{{ panelTitle }}</h2>

      <GroupForm :schema="groupSchema" v-model="groupData" />

      <div class="form-actions">
          <button @click="step = 1" class="btn-option">Back</button>

          <button type="button" class="btn-option" @click="requestSaveAndContinue" :disabled="unsavedBusy || !saveDirty">
            {{ unsavedBusy ? "Saving…" : (isPublishedStudy ? "Review and Save Changes" : "Save and Continue") }}
          </button>

          <button
            v-if="!isEditing"
            type="button"
            class="btn-option"
            @click="onUnsavedSaveAndExit"
            :disabled="unsavedBusy"
          >
            {{ unsavedBusy ? "Saving…" : "Save Draft and Leave" }}
          </button>

          <button v-if="isEditing && !isPublishedStudy" type="button" class="btn-option" @click="saveThisStepAndBackToStudy">
            {{ isPublishedStudy ? "Review and save changes" : "Save and Leave" }}
          </button>

          <button @click="checkGroups()" class="btn-option">Next</button>
        </div>
    </div>

    <!-- STEP 3 -->
    <div v-if="step === 3" class="new-study-form">
      <h2>{{ panelTitle }}</h2>

      <div class="step-header">
        <label class="skip-toggle">
          <span class="skip-label">Skip subject creation for now</span>
          <button
            type="button"
            class="toggle-button"
            :class="{ 'toggle-on': skipSubjectCreationNow }"
            @click="skipSubjectCreationNow = !skipSubjectCreationNow"
          >
            <span class="toggle-knob" />
          </button>
        </label>
      </div>

      <div v-if="isPublishedStudy" class="published-step-note">
          This study is published. Subject ID format is inferred from existing subject IDs and locked, but subject count and assignment method can still be updated.
        </div>

      <SubjectForm
          v-model:subjectCount="subjectCount"
          v-model:assignmentMethod="assignmentMethod"
          v-model:subjectIdConfig="subjectIdConfig"
          :subjectSetupEditable="true"
          :subjectIdFormatEditable="!isPublishedStudy"
          :hasExistingSubjects="hasExistingSubjects"
          :isPublished="isPublishedStudy"
          :lockedSubjectIds="persistedSubjects.map(subject => subject.id || subject.subject_id)"
          @skip-manual="skipManualEnrollment"
          @changed="onSubjectSetupChanged"
        />
      <div class="form-actions">
          <button @click="step = 2" class="btn-option">Back</button>

          <button type="button" class="btn-option" @click="requestSaveAndContinue" :disabled="unsavedBusy || !saveDirty">
            {{ unsavedBusy ? "Saving…" : (isPublishedStudy ? "Review and Save Changes" : "Save and Continue") }}
          </button>

          <button
            v-if="!isEditing"
            type="button"
            class="btn-option"
            @click="onUnsavedSaveAndExit"
            :disabled="unsavedBusy"
          >
            {{ unsavedBusy ? "Saving…" : "Save Draft and Leave" }}
          </button>

          <button v-if="isEditing && !isPublishedStudy" type="button" class="btn-option" @click="saveThisStepAndBackToStudy">
            {{ isPublishedStudy ? "Review and save changes" : "Save and Leave" }}
          </button>

          <button @click="checkSubjectsSetup()" class="btn-option">
            Next
          </button>
        </div>
    </div>

    <!-- STEP 4 -->
    <div v-if="step === 4 && assignmentMethod !== 'Skip' && !skipSubjectCreationNow" class="new-study-form">
      <h2>{{ panelTitle }}</h2>

      <SubjectAssignmentForm :subjects="subjectData" :groupData="groupData" v-model:subjects="subjectData" @changed="onSubjectSetupChanged"/>

      <div class="form-actions">
          <button @click="step = 3" class="btn-option">Back</button>

          <button type="button" class="btn-option" @click="requestSaveAndContinue" :disabled="unsavedBusy || !saveDirty">
            {{ unsavedBusy ? "Saving…" : (isPublishedStudy ? "Review and Save Changes" : "Save and Continue") }}
          </button>

          <button
            v-if="!isEditing"
            type="button"
            class="btn-option"
            @click="onUnsavedSaveAndExit"
            :disabled="unsavedBusy"
          >
            {{ unsavedBusy ? "Saving…" : "Save Draft and Leave" }}
          </button>

          <button v-if="isEditing && !isPublishedStudy" type="button" class="btn-option" @click="saveThisStepAndBackToStudy">
            {{ isPublishedStudy ? "Review and save changes" : "Save and Leave" }}
          </button>

          <button @click="checkSubjectsAssigned()" class="btn-option">Next</button>
        </div>
    </div>

    <!-- STEP 5 -->
    <div v-if="step === 5" class="new-study-form">
      <h2>{{ panelTitle }}</h2>

      <VisitForm :schema="visitSchema" v-model="visitData" />

      <div class="form-actions">
          <button @click="goBackFromVisits" class="btn-option">Back</button>

          <button type="button" class="btn-option" @click="requestSaveAndContinue" :disabled="unsavedBusy || !saveDirty">
            {{ unsavedBusy ? "Saving…" : (isPublishedStudy ? "Review and Save Changes" : "Save and Continue") }}
          </button>

          <button
            v-if="!isEditing"
            type="button"
            class="btn-option"
            @click="onUnsavedSaveAndExit"
            :disabled="unsavedBusy"
          >
            {{ unsavedBusy ? "Saving…" : "Save Draft and Leave" }}
          </button>

          <button v-if="isEditing && !isPublishedStudy" type="button" class="btn-option" @click="saveThisStepAndBackToStudy">
            {{ isPublishedStudy ? "Review and save changes" : "Save and Leave" }}
          </button>

          <button @click="goToFinish()" class="btn-option">Forms</button>
        </div>
    </div>
  </div>

  <!-- Dialog -->
  <div v-if="showDialog" class="dialog-backdrop" @click.self="dialogMode !== 'unsaved' ? closeDialog() : null">
    <div class="dialog-box">
      <p>{{ dialogMessage }}</p>

      <!-- Unsaved changes dialog -->
      <div
        v-if="dialogMode === 'unsaved'"
        class="dialog-actions"
        style="grid-template-columns: repeat(3, minmax(0, 1fr));"
      >
        <button class="btn-option" @click="onUnsavedKeepEditing" :disabled="unsavedBusy">Keep editing</button>

        <button class="btn-option" @click="onUnsavedExitWithoutSaving" :disabled="unsavedBusy">Exit without saving</button>

        <button class="btn-option" @click="onUnsavedSaveAndExit" :disabled="unsavedBusy">
          {{ unsavedBusy ? "Saving…" : saveExitLabel }}
        </button>
      </div>

      <!-- Import success dialog actions -->
      <div v-else-if="dialogMode === 'importSuccess'" class="dialog-actions">
        <button class="btn-option" @click="goDashboardAfterImport">Go to Dashboard</button>
        <button class="btn-option" @click="viewImportedStudy">View Study</button>
        <button class="btn-option" @click="editImportedStudy">Edit Study</button>
        <button class="btn-option" @click="closeDialog">Close</button>
      </div>

      <div v-else-if="dialogMode === 'publishedSave'" class="dialog-actions">
        <button class="btn-option" @click="closeDialog" :disabled="unsavedBusy">Cancel</button>
        <button class="btn-option" @click="confirmPublishedSaveAndContinue" :disabled="unsavedBusy">
          {{ unsavedBusy ? "Saving…" : "Review and save changes" }}
        </button>
      </div>

      <button v-else @click="closeDialog" class="btn-option">OK</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, inject, computed, watch, onBeforeUnmount } from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import { useStore } from "vuex";
import axios from "axios";
import yaml from "js-yaml";

import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseTextarea from "@/components/forms/BaseTextarea.vue";
import BaseNumberField from "@/components/forms/BaseNumberField.vue";
import BaseDateField from "@/components/forms/BaseDateField.vue";
import BaseSelectField from "@/components/forms/BaseSelectField.vue";
import { STUDY_BEFORE_IDLE_EVENT } from "@/utils/studySaveWorkflow";
import { normalizeStudySettings } from "@/utils/studySettings";

import GroupForm from "./GroupForm.vue";
import VisitForm from "./VisitForm.vue";
import SubjectForm from "./SubjectForm.vue";
import SubjectAssignmentForm from "./SubjectAssignmentForm.vue";
import {
  createDefaultSubjectIdConfig,
  normalizeSubjectIdConfig,
  inferSubjectIdConfigFromExistingSubjects,
  buildUniqueSubjectId,
  getNextSubjectSequenceNumber,
  subjectIdPatternValidationMessage,
} from "@/utils/subjectIdUtils";
import { reconcileManualSubjects } from "@/utils/manualSubjectIds";

export default {
  name: "StudyCreationComponent",
  components: {
    BaseTextField,
    BaseTextarea,
    BaseNumberField,
    BaseDateField,
    BaseSelectField,
    GroupForm,
    VisitForm,
    SubjectForm,
    SubjectAssignmentForm,
  },
  props: {
    id: { type: [String, Number], required: false },
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const formatLabel = inject("formatLabel");

    const step = ref(1);
    const studyData = ref({});
    const groupData = ref([]);
    const subjectData = ref([]);
    const persistedSubjects = ref([]);
    const visitData = ref([]);
    const subjectCount = ref(1);
    const assignmentMethod = ref("Random");
    const assignments = ref([]);
    const subjectIdConfig = ref(createDefaultSubjectIdConfig({}, "Study"));
    const studySchema = ref([]);
    const groupSchema = ref([]);
    const visitSchema = ref([]);

    const showStudyErrors = ref(false);
    const showDialog = ref(false);
    const dialogMessage = ref("");
    const dialogMode = ref("default"); // 'default' | 'importSuccess' | 'unsaved'
    const unsavedBusy = ref(false);
    const lastSavedAt = ref(null);
    const saveError = ref("");

    const skipSubjectCreationNow = ref(false);

    // always show unsaved dialog on navigation/logout/logo/window close (irrespective of status)
    const guardEnabled = computed(() => true);

    // --- import template state ---
    const templateFileInput = ref(null);
    const importingTemplate = ref(false);
    const templateFileName = ref("");
    const templateImportError = ref("");

    // store created study info (for dialog actions)
    const importedStudyId = ref(null);
    const importedStudyName = ref("");

    // draft creation happens ONLY on Save & Exit (or explicit Save button in edit)
    const draftStudyId = ref(null);

    // unsaved tracking + route interception
    const lastSavedSnapshot = ref(null);
    const isDirty = ref(false);
    const allowInternalNav = ref(false);

    const stepErrors = ref({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: false });

    const editId = computed(() => props.id || route.params.id || null);
    const isEditing = computed(() => !!editId.value);
    const storedStudyStatus = computed(() =>
      String(
        store.state.studyDetails?.study_metadata?.status ||
        store.state.studyDetails?.metadata?.status ||
        store.state.studyDetails?.status ||
        ""
      ).trim().toUpperCase()
    );
    const isPublishedStudy = computed(() => storedStudyStatus.value === "PUBLISHED");
    const currentStudyStatus = computed(() =>
      storedStudyStatus.value || (isEditing.value ? "UNKNOWN" : "DRAFT")
    );
    const saveDirty = computed(() => !!isDirty.value || !!globalDirty.value);
    const canAutoSaveDraft = computed(() => {
      const s = studyData.value || {};
      return currentStudyStatus.value === "DRAFT" && !!String(s.title || s.study_name || s.name || "").trim();
    });

    const hasExistingSubjects = computed(() => {
      return Array.isArray(subjectData.value) && subjectData.value.some((s) => s?.id || s?.subject_id);
    });

    const saveExitLabel = computed(() => {
      // keep wording stable, but behavior is now: save (draft if new; update if editing) then exit
      return isEditing.value ? "Save & Exit" : "Save as Draft & Exit";
    });

    const stepLabelById = {
      1: "Create a New Study",
      2: "Groups",
      3: "Subject Setup",
      4: "Group Assignment",
      5: "Visits",
      6: "Forms",
    };

    // keep previous titles in create mode
    const createTitleById = {
      1: "Step 1: Create a New Study",
      2: "Step 2: Groups",
      3: "Step 3: Subject Setup",
      4: "Step 4: Group Assignment",
      5: "Step 5: Visits",
      6: "Step 6: Forms",
    };

    const currentStepLabel = computed(() => stepLabelById[step.value] || "");

    const headerStudyName = computed(() => {
      const s = studyData.value || {};
      return (
        s.title ||
        s.study_name ||
        s.name ||
        (store.state.studyDetails?.study_metadata?.name ?? "") ||
        "Study"
      );
    });

    const panelTitle = computed(() => {
      if (isEditing.value) return `Editing ${headerStudyName.value}, Step ${currentStepLabel.value}`;
      return createTitleById[step.value] || "";
    });

    const nextLabelFromStep3 = computed(() => {
      const disableAssign = skipSubjectCreationNow.value || assignmentMethod.value === "Skip";
      return disableAssign ? stepLabelById[5] : stepLabelById[4];
    });

    const prevLabelForVisits = computed(() => {
      const disableAssign = skipSubjectCreationNow.value || assignmentMethod.value === "Skip";
      return disableAssign ? stepLabelById[3] : stepLabelById[4];
    });

    const token = computed(() => store.state.token);
    const authHeader = computed(() => ({ Authorization: `Bearer ${token.value}` }));
    const globalDirty = computed(() => !!store.state.studyCreationDirty);
    const currentUserId = computed(() => store.state.user?.id || null);

    function onSubjectSetupChanged(event) {
      if (event?.kind === "manualSubjectIds" && (subjectIdConfig.value.manualIds || []).length > persistedSubjects.value.length) {
        skipSubjectCreationNow.value = false;
      }
      // Mark Step 4 validation as stale whenever subject setup/assignment changes.
      // Next click to Step 5 will re-run checkSubjectsAssigned() and block if needed.
      const step4Applies = !skipSubjectCreationNow.value && assignmentMethod.value !== "Skip";
      if (step4Applies) {
        stepErrors.value[4] = true;
      } else {
        stepErrors.value[4] = false;
      }
    }
    function closeDialog() {
      showDialog.value = false;
      dialogMode.value = "default";
      dialogMessage.value = "";
    }

    function openUnsavedDialog(message) {
      dialogMode.value = "unsaved";
      dialogMessage.value =
        message ||
        "You have unsaved changes. You can keep editing, exit without saving, or save your changes and return to the Dashboard.";
      showDialog.value = true;
    }

    function fieldError(f, value) {
      return f.required && showStudyErrors.value && !value ? `${f.label} is required.` : "";
    }

    async function loadYaml(path, schemaRef) {
      const res = await fetch(path);
      const doc = yaml.load(await res.text());
      const cls = Object.keys(doc.classes)[0];
      const attrs = doc.classes[cls].attributes;
      schemaRef.value = Object.entries(attrs).map(([n, d]) => {
        let type = d.widget === "textarea" ? "textarea" : "text";
        const r = (d.range || "").toLowerCase();
        if (r === "date" || r === "datetime") type = "date";
        if (r === "integer" || r === "decimal") type = "number";
        if (d.enum) type = "select";
        return {
          field: n,
          label: formatLabel(n),
          placeholder: d.description || formatLabel(n),
          type,
          required: !!d.required,
          disabled: !!d.disabled,
          display: d.display !== false,
          options: d.enum || [],
          skip: d.skip || {},
        };
      });
    }

    function initializeAssignments(models, visits, groups) {
      return Array(models.length)
        .fill()
        .map(() =>
          Array(visits.length)
            .fill()
            .map(() => Array(groups.length).fill(false))
        );
    }

    function _deepClone(x) {
      try {
        return JSON.parse(JSON.stringify(x));
      } catch (err) {
        return x;
      }
    }

    function getSubjectSequenceNumberForIndex(index) {
      const cfg = normalizeSubjectIdConfig(
        subjectIdConfig.value,
        studyData.value || {},
        headerStudyName.value || "Study"
      );

      return Number(cfg.startNumber || 1) + Number(index || 0);
    }

    function buildUniqueSubjectIdForCurrentStudy(config, sequenceNumber, existingIds) {
      return buildUniqueSubjectId(
        config,
        sequenceNumber,
        existingIds,
        studyData.value || {},
        headerStudyName.value || "Study"
      );
    }

    function uuidForImport() {
      if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
      return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }

    function ensureIdsInSelectedModels(selectedModels) {
      if (!Array.isArray(selectedModels)) return [];

      return selectedModels.map((sec, sIdx) => ({
        ..._deepClone(sec),
        _id: sec?._id || sec?.id || uuidForImport(),
        title: sec?.title || `Section ${sIdx + 1}`,
        fields: Array.isArray(sec?.fields)
          ? sec.fields.map((field, fIdx) => ({
              ..._deepClone(field),
              _id: field?._id || field?.id || uuidForImport(),
              name: field?.name || `field_${sIdx}_${fIdx}`,
              constraints: field?.constraints || {},
            }))
          : [],
      }));
    }

    function selectedModelsToForms(selectedModels) {
      const normalized = ensureIdsInSelectedModels(selectedModels);
      return [
        {
          sections: normalized.map((sec) => ({
            _id: sec._id,
            title: sec.title,
            fields: _deepClone(sec.fields || []),
            collapsed: !!sec.collapsed,
            source: sec.source || "template",
          })),
        },
      ];
    }

    function buildSnapshot() {
      return _deepClone({
        step: step.value,
        studyData: studyData.value || {},
        groupData: groupData.value || [],
        subjectData: subjectData.value || [],
        visitData: visitData.value || [],
        subjectCount: subjectCount.value,
        assignmentMethod: assignmentMethod.value,
        subjectIdConfig: subjectIdConfig.value || null,
        assignments: assignments.value || [],
        skipSubjectCreationNow: !!skipSubjectCreationNow.value,
        draftStudyId: draftStudyId.value,
        editId: editId.value,
      });
    }

    function markSavedSnapshot(recordSaveTime = false) {
      lastSavedSnapshot.value = buildSnapshot();
      isDirty.value = false;
      store.commit("setStudyCreationDirty", false);
      if (recordSaveTime) lastSavedAt.value = Date.now();
      saveError.value = "";
    }

    function markSavedRequestSnapshot(serializedSnapshot, createdDraftId = null) {
      let savedSnapshot;
      try {
        savedSnapshot = JSON.parse(serializedSnapshot);
      } catch {
        savedSnapshot = buildSnapshot();
      }
      if (createdDraftId != null) savedSnapshot.draftStudyId = Number(createdDraftId);

      lastSavedSnapshot.value = savedSnapshot;
      lastSavedAt.value = Date.now();
      saveError.value = "";
      computeDirty();
      if (!isDirty.value) store.commit("setStudyCreationDirty", false);
    }

    function computeDirty() {
      try {
        const a = JSON.stringify(buildSnapshot());
        const b = JSON.stringify(lastSavedSnapshot.value || {});
        isDirty.value = a !== b;
      } catch {
        isDirty.value = true;
      }
      if (isDirty.value) store.commit("setStudyCreationDirty", true);
    }

    /* ============================================================
       FORMS PRESERVATION FIX
       - Preserve already-built ScratchForm/template data when saving
         from StudyCreationComponent (even if user only changed step 1).
       ============================================================ */
    function parseScratchFormsFromLocalStorage() {
      try {
        const parsed = JSON.parse(localStorage.getItem("scratchForms") || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    function normalizeFormsArray(candidate) {
      if (!Array.isArray(candidate)) return [];
      return candidate
        .filter((f) => f && typeof f === "object")
        .map((f) => ({
          ...f,
          sections: Array.isArray(f.sections)
            ? f.sections.map((sec, sIdx) => ({
                ...sec,
                _id: sec?._id || sec?.id || uuidForImport(),
                title: sec?.title ?? "Untitled Section",
                fields: Array.isArray(sec?.fields)
                  ? sec.fields.map((field, fIdx) => ({
                      ...field,
                      _id: field?._id || field?.id || uuidForImport(),
                      name: field?.name || `field_${sIdx}_${fIdx}`,
                      constraints: field?.constraints || {},
                    }))
                  : [],
                source: sec?.source || "template",
              }))
            : [],
        }));
    }

    function deriveSelectedModelsFromForms(formsArr) {
      const first = Array.isArray(formsArr) && formsArr.length ? formsArr[0] : null;
      const sections = Array.isArray(first?.sections) ? first.sections : [];
      return sections.map((sec, sIdx) => ({
        _id: sec?._id || sec?.id || uuidForImport(),
        title: sec?.title || `Section ${sIdx + 1}`,
        fields: Array.isArray(sec?.fields)
          ? sec.fields.map((field, fIdx) => ({
              ..._deepClone(field),
              _id: field?._id || field?.id || uuidForImport(),
              name: field?.name || `field_${sIdx}_${fIdx}`,
              constraints: field?.constraints || {},
            }))
          : [],
      }));
    }

    function getFormsForSavePayload() {
      const details = store.state.studyDetails || {};

      // 1) Prefer store.forms if present (most stable when draft was hydrated from backend)
      const storeForms = normalizeFormsArray(details.forms);

      // 2) Fallback to local scratch cache (when ScratchForm was used and store got partially overwritten)
      const lsForms = normalizeFormsArray(parseScratchFormsFromLocalStorage());

      // 3) Fallback to store.selectedModels (convert to forms shape)
      let selectedModelDerived = [];
      if (Array.isArray(details.selectedModels) && details.selectedModels.length) {
        selectedModelDerived = selectedModelsToForms(details.selectedModels);
      }

      const selectedModelDerivedNorm = normalizeFormsArray(selectedModelDerived);

      const picked =
        (storeForms.length ? storeForms : null) ||
        (lsForms.length ? lsForms : null) ||
        (selectedModelDerivedNorm.length ? selectedModelDerivedNorm : null) ||
        [{ sections: [] }];

      return _deepClone(picked);
    }

    function getSelectedModelsForSavePayload() {
      return deriveSelectedModelsFromForms(getFormsForSavePayload());
    }

    function commitStudyDetailsPreservingForms(partial) {
      const existing = store.state.studyDetails || {};
      const formsToPersist = getFormsForSavePayload();

      store.commit("setStudyDetails", {
        ...existing,
        ...partial,
        forms: formsToPersist,
      });
    }

    function populateRefsFromStore() {
      const details = store.state.studyDetails || {};

      if (details && details.study && Object.keys(details.study).length) {
        studyData.value = { ...details.study };
      } else {
        // keep keys to avoid undefined bindings
        const base = {};
        (studySchema.value || []).forEach((f) => (base[f.field] = ""));
        studyData.value = base;
      }

      groupData.value = Array.isArray(details.groups) ? [...details.groups] : [];
      subjectCount.value = details.subjectCount ?? subjectCount.value;
      assignmentMethod.value = details.assignmentMethod ?? assignmentMethod.value;
      subjectData.value = Array.isArray(details.subjects) ? [...details.subjects] : [];
      persistedSubjects.value = isEditing.value ? _deepClone(subjectData.value) : [];
      visitData.value = Array.isArray(details.visits) ? [...details.visits] : [];
      skipSubjectCreationNow.value = !!details.skipSubjectCreationNow;

      subjectIdConfig.value = details.subjectIdConfig
        ? normalizeSubjectIdConfig(
            details.subjectIdConfig,
            studyData.value || {},
            headerStudyName.value || "Study"
          )
        : inferSubjectIdConfigFromExistingSubjects(
            subjectData.value,
            studyData.value || {},
            headerStudyName.value || "Study",
            { useLast: false }
          );

      subjectIdConfig.value.locked = !!isPublishedStudy.value;
      if (isEditing.value && subjectIdConfig.value.mode === "manual") {
        subjectIdConfig.value.manualIds = subjectData.value.map(subject => subject.id || subject.subject_id);
      }

      assignments.value = Array.isArray(details.assignments)
        ? JSON.parse(JSON.stringify(details.assignments))
        : initializeAssignments(details.selectedModels || [], details.visits || [], details.groups || []);
      // When editing a draft, subjects can be empty even though subject creation isn't skipped.
      // Reuse the original generator logic (no dialogs, no step change).
      const disableSubjects = !!skipSubjectCreationNow.value || assignmentMethod.value === "Skip";
      if (!disableSubjects && (!Array.isArray(subjectData.value) || subjectData.value.length === 0)) {
        checkSubjectsSetup({ advance: false, silent: true });
      }
    }

    function buildBackendPayload() {
      const s = studyData.value || {};
      const study_name = s.title || s.study_name || s.name || "Untitled Study";
      const study_description = s.description || s.study_description || "";

      // CRITICAL: preserve ScratchForm/template data even if step-1 only edits happened
      const selectedModelsToPersist = getSelectedModelsForSavePayload();

      return {
        study_metadata: {
          created_by: currentUserId.value,
          study_name,
          study_description,
        },
        study_content: {
          study_data: {
            study: _deepClone(studyData.value || {}),
            studySettings: normalizeStudySettings(store.state.studyDetails?.studySettings),
            groups: _deepClone(groupData.value || []),
            visits: _deepClone(visitData.value || []),
            subjectCount: Number(subjectCount.value || 0),
            assignmentMethod: assignmentMethod.value || "Random",
            subjectIdConfig: _deepClone(subjectIdConfig.value || null),
            subjects: _deepClone(subjectData.value || []),
            assignments: _deepClone(assignments.value || []),
            skipSubjectCreationNow: !!skipSubjectCreationNow.value,
            selectedModels: _deepClone(selectedModelsToPersist || []),
          },
        },
      };
    }

    // save logic used ONLY by:
    // - Save button in edit mode
    // - Unsaved dialog "Save & Exit"
    async function saveNow({ automatic = false, action = "continue" } = {}) {
      if (!token.value) {
        router.push("/login");
        return false;
      }

      if (subjectIdConfig.value.mode === "manual" && !skipSubjectCreationNow.value &&
          (subjectIdConfig.value.manualIds || []).length && !checkSubjectsSetup({ advance: false })) return false;
      const payload = buildBackendPayload();
      const formsToPersist = getFormsForSavePayload();
      const existingStudyId = editId.value || draftStudyId.value;
      const saveStartSnapshot = JSON.stringify(buildSnapshot());

      try {
        if (existingStudyId) {
          await axios.put(
              `/forms/studies/${existingStudyId}`,
              payload,
              {
                headers: authHeader.value,
                __skipActivityTracker: automatic,
                // audit_label: user clicked "Save" / "Save & Exit" while editing an existing study
                params: {
                  audit_label: automatic
                    ? "Automatic Draft Recovery Save"
                    : action === "exit" ? "Update Existing Study" : "Save and Continue",
                },
              }
            );

          // Keep local store consistent and preserve forms/template after save
          persistedSubjects.value = _deepClone(payload.study_content.study_data.subjects || []);
          commitStudyDetailsPreservingForms({
            study_metadata: {
              ...(store.state.studyDetails?.study_metadata || {}),
              id: Number(existingStudyId),
              name: payload.study_metadata.study_name,
              study_name: payload.study_metadata.study_name,
              description: payload.study_metadata.study_description,
              study_description: payload.study_metadata.study_description,
            },
            study: {
              ...(store.state.studyDetails?.study || {}),
              ..._deepClone(payload.study_content.study_data.study || {}),
              id: Number(existingStudyId),
            },
            groups: _deepClone(payload.study_content.study_data.groups || []),
            visits: _deepClone(payload.study_content.study_data.visits || []),
            subjectCount: payload.study_content.study_data.subjectCount || 0,
            assignmentMethod: payload.study_content.study_data.assignmentMethod || "Random",
            subjectIdConfig: _deepClone(payload.study_content.study_data.subjectIdConfig || null),
            subjects: _deepClone(payload.study_content.study_data.subjects || []),
            assignments: _deepClone(payload.study_content.study_data.assignments || []),
            skipSubjectCreationNow: !!payload.study_content.study_data.skipSubjectCreationNow,
            selectedModels: _deepClone(payload.study_content.study_data.selectedModels || deriveSelectedModelsFromForms(formsToPersist)),
          });

          markSavedRequestSnapshot(saveStartSnapshot);
          return true;
        }

        // create new: create as DRAFT
        const resp = await axios.post(
          `/forms/studies/?status=DRAFT&last_completed_step=${encodeURIComponent(String(step.value))}`,
          payload,
          {
            headers: authHeader.value,
            __skipActivityTracker: automatic,
            // audit_label: user clicked "Save as Draft & Exit" (or "Save & Exit" in create flow)
            params: {
              audit_label: automatic
                ? "Automatic Draft Recovery Save"
                : action === "exit" ? "Save Draft of Study" : "Save and Continue - Create Draft",
            },
          }
        );

        const meta = resp.data?.metadata || resp.data?.study_metadata || {};
        const createdId = meta?.id ?? resp.data?.id;
        if (createdId == null) throw new Error("Draft created, but ID was not returned.");

        draftStudyId.value = Number(createdId);

        commitStudyDetailsPreservingForms({
          study_metadata: {
            id: Number(createdId),
            name: meta.study_name || payload.study_metadata.study_name,
            study_name: meta.study_name || payload.study_metadata.study_name,
            description: meta.study_description || payload.study_metadata.study_description,
            study_description: meta.study_description || payload.study_metadata.study_description,
            created_by: meta.created_by || payload.study_metadata.created_by,
            created_at: meta.created_at,
            updated_at: meta.updated_at,
            status: String(meta.status || "DRAFT").toUpperCase(),
          },
          study: { ...(payload.study_content.study_data.study || {}), id: Number(createdId) },
          groups: payload.study_content.study_data.groups || [],
          visits: payload.study_content.study_data.visits || [],
          subjectCount: payload.study_content.study_data.subjectCount || 0,
          assignmentMethod: payload.study_content.study_data.assignmentMethod || "Random",
          subjectIdConfig: _deepClone(payload.study_content.study_data.subjectIdConfig || null),
          subjects: payload.study_content.study_data.subjects || [],
          assignments: payload.study_content.study_data.assignments || [],
          skipSubjectCreationNow: !!payload.study_content.study_data.skipSubjectCreationNow,
          selectedModels: _deepClone(payload.study_content.study_data.selectedModels || deriveSelectedModelsFromForms(formsToPersist)),
        });

        markSavedRequestSnapshot(saveStartSnapshot, createdId);
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to save.";
        saveError.value = String(msg);
        if (!automatic) {
          dialogMessage.value = String(msg);
          dialogMode.value = "default";
          showDialog.value = true;
        }
        return false;
      }
    }

    async function performSaveAndContinue({ automatic = false } = {}) {
      if (unsavedBusy.value || !saveDirty.value) return;
      unsavedBusy.value = true;
      saveError.value = "";
      try {
        await saveNow({ automatic });
      } finally {
        unsavedBusy.value = false;
      }
    }

    function requestSaveAndContinue() {
      if (isPublishedStudy.value) {
        dialogMode.value = "publishedSave";
        dialogMessage.value =
          "This study is published. Saving structural changes may create a new template version. Review the changes before saving.";
        showDialog.value = true;
        return;
      }
      performSaveAndContinue();
    }

    async function confirmPublishedSaveAndContinue() {
      closeDialog();
      await performSaveAndContinue();
    }

    function saveDraftBeforeIdle() {
      if (!canAutoSaveDraft.value || isPublishedStudy.value) return;
      performSaveAndContinue({ automatic: true });
    }
    function isReturningFromScratch() {
      return route.name === "CreateStudy" && route.query.step != null;
    }

    function validateStepOnly(stepId) {
      if (stepId === 1) return validateStudy({ advance: false });
      if (stepId === 2) return checkGroups({ advance: false });
      if (stepId === 3) return checkSubjectsSetup({ advance: false });
      if (stepId === 4) return checkSubjectsAssigned({ advance: false });
      if (stepId === 5) return checkVisits();
      return true;
    }

    function resolveReturnRoute() {
      const returnTo = route.query.returnTo ? String(route.query.returnTo) : "StudyView";
      const returnId =
        route.query.returnId != null
          ? String(route.query.returnId)
          : editId.value != null
          ? String(editId.value)
          : "";
      const returnTab = route.query.returnTab ? String(route.query.returnTab) : null;

      const nav = { name: returnTo };
      if (returnId) nav.params = { id: returnId };
      if (returnTab) nav.query = { tab: returnTab };
      return nav;
    }

    async function saveThisStepAndBackToStudy() {
      const ok = validateStepOnly(step.value);
      if (!ok) return;

      if (isPublishedStudy.value) {
        requestSaveAndContinue();
        return;
      }

      const saved = await saveNow({ action: "exit" });
      if (!saved) return;

      router.push(resolveReturnRoute());
    }

    function openFinishRoute() {
      const q = { ...route.query };
      if (isEditing.value) {
        q.mode = q.mode || "edit";
        q.single = q.single || "true";
        q.returnTo = q.returnTo || "StudyView";
        q.returnId = q.returnId || (editId.value != null ? String(editId.value) : "");
        q.returnTab = q.returnTab || "edit";
      }

      allowInternalNav.value = true;
      router
        .push({
          name: "CreateFormScratch",
          params: { assignments: assignments.value },
          query: q,
        })
        .finally(() => {
          allowInternalNav.value = false;
        });
    }

    // -----------------------
    // Import Study Template (Step 1, create mode)
    // -----------------------
    function triggerTemplatePick() {
      templateImportError.value = "";
      templateFileName.value = "";
      const el = templateFileInput.value;
      if (el) el.click();
    }

    function openStudyDataImport() {
      allowInternalNav.value = true;
      router
        .push({
          name: "Dashboard",
          query: { view: "import-data", returnTo: "create-study" },
        })
        .finally(() => {
          allowInternalNav.value = false;
        });
    }

    async function onTemplatePicked(e) {
      const f = e?.target?.files && e.target.files[0];
      if (e?.target) e.target.value = "";
      if (!f) return;
      await importStudyTemplateFile(f);
    }

    function normalizeImportedStudyData(rawJson) {
      const candidate =
        rawJson?.study_data && typeof rawJson.study_data === "object" ? rawJson.study_data : rawJson;
      const sd = typeof candidate === "object" && candidate ? candidate : null;
      if (!sd || !Array.isArray(sd.selectedModels)) {
        throw new Error("Invalid template format. Missing selectedModels.");
      }
      return sd;
    }

    function buildAssignmentsIfMissing(sd) {
      const existing = Array.isArray(sd.assignments) ? sd.assignments : [];
      if (existing.length) return existing;

      const models = Array.isArray(sd.selectedModels) ? sd.selectedModels : [];
      const visits = Array.isArray(sd.visits) ? sd.visits : [];
      const groups = Array.isArray(sd.groups) ? sd.groups : [];
      return initializeAssignments(models, visits, groups);
    }

    function setScratchFormsFromSelectedModels(selectedModels) {
      if (!Array.isArray(selectedModels)) return;
      const scratchForms = selectedModelsToForms(selectedModels);
      localStorage.setItem("scratchForms", JSON.stringify(scratchForms));
    }

    function setScratchForms(forms, selectedModels = []) {
      const normalizedForms = normalizeFormsArray(forms);
      if (normalizedForms.length) {
        localStorage.setItem("scratchForms", JSON.stringify(normalizedForms));
        return;
      }
      setScratchFormsFromSelectedModels(selectedModels);
    }

    function mergeMetaIntoStudyNode(studyNode, meta) {
      const base = _deepClone(studyNode || {});
      const metaName = meta?.study_name || meta?.name || "";
      const metaDesc = meta?.study_description || meta?.description || "";

      const titleVal = base.title || base.study_name || base.name || metaName;
      const descVal = base.description || base.study_description || metaDesc;

      return {
        ...base,
        title: titleVal,
        name: titleVal,
        study_name: titleVal,
        description: descVal,
        study_description: descVal,
      };
    }

    async function hydrateStoreFromStudyId(id) {
      const resp = await axios.get(`/forms/studies/${id}`, { headers: authHeader.value });

      const sd = resp.data?.content?.study_data || {};
      const meta = resp.data?.metadata || resp.data?.study_metadata || {};

      let assignmentsLocal = Array.isArray(sd.assignments) ? sd.assignments : [];
      if (!assignmentsLocal.length && sd.selectedModels?.length) {
        assignmentsLocal = buildAssignmentsIfMissing(sd);
      }

      const normalizedSelectedModels = ensureIdsInSelectedModels(sd.selectedModels || []);
      const savedForms = normalizeFormsArray(sd.forms);
      const normalizedForms =
        savedForms.length
          ? savedForms
          : selectedModelsToForms(normalizedSelectedModels);

      const studyInfo = {
        id: meta.id ?? id,
        name: meta.study_name,
        description: meta.study_description,
        created_at: meta.created_at,
        updated_at: meta.updated_at,
        created_by: meta.created_by,
        status: meta.status,
      };

      const mergedStudy = mergeMetaIntoStudyNode(sd.study, meta);

      store.commit("setStudyDetails", {
        study_metadata: studyInfo,
        study: { id: meta.id ?? id, ...mergedStudy },
        groups: sd.groups || [],
        visits: sd.visits || [],
        subjectCount: sd.subjectCount || 0,
        assignmentMethod: sd.assignmentMethod || "random",
        subjectIdConfig: _deepClone(sd.subjectIdConfig || null),
        subjects: sd.subjects || [],
        assignments: assignmentsLocal,
        skipSubjectCreationNow: !!sd.skipSubjectCreationNow,
        selectedModels: _deepClone(normalizedSelectedModels),
        forms: _deepClone(normalizedForms),
        studySettings: normalizeStudySettings(sd.studySettings || sd.study_settings),
      });

      setScratchForms(normalizedForms, normalizedSelectedModels);
    }

    async function importStudyTemplateFile(file) {
      templateImportError.value = "";
      templateFileName.value = file?.name || "";

      if (!token.value) {
        templateImportError.value = "Please log in again.";
        router.push("/login");
        return;
      }

      importingTemplate.value = true;
      try {
        const text = await file.text();
        const raw = JSON.parse(text);
        const sd = normalizeImportedStudyData(raw);

        const studyObj = sd.study || {};
        const studyName = studyObj.title || studyObj.study_name || raw.study_name || "Imported Study";
        const studyDescription =
          studyObj.description || studyObj.study_description || raw.study_description || "";

        const study_metadata = {
          created_by: currentUserId.value,
          study_name: studyName,
          study_description: studyDescription,
        };

        const original = _deepClone(sd);
        const normalizedSelectedModels = ensureIdsInSelectedModels(original.selectedModels || []);

        const studyNode = {
          ...(original.study || {}),
          title: studyName,
          name: studyName,
          study_name: studyName,
          description: studyDescription,
          study_description: studyDescription,
        };

        const study_data = {
          ...original,
          study: studyNode,
          selectedModels: normalizedSelectedModels,
          assignments: buildAssignmentsIfMissing({
            ...original,
            selectedModels: normalizedSelectedModels,
          }),
        };

        const payload = {
          study_metadata,
          study_content: { study_data },
        };

        const created = await axios.post(
          "/forms/studies/",
          payload,
          {
            headers: authHeader.value,
            // audit_label: user imported a template JSON and created a new study from it
            params: { audit_label: "Study Creation by Import Template" },
          }
        );
        const meta = created.data?.study_metadata || created.data?.metadata || {};
        const createdId = meta.id ?? created.data?.id;
        if (createdId == null) throw new Error("Study created but ID was not returned.");

        await hydrateStoreFromStudyId(createdId);

        importedStudyId.value = String(createdId);
        importedStudyName.value = String(meta.study_name || studyName || "Imported Study");

        dialogMode.value = "importSuccess";
        dialogMessage.value = `Imported study “${importedStudyName.value}” is saved successfully. What would you like to do next?`;
        showDialog.value = true;
      } catch (err) {
        console.error(err);
        templateImportError.value =
          err?.message || "Failed to import JSON. Please select a valid template file.";
      } finally {
        importingTemplate.value = false;
      }
    }

    function goDashboardAfterImport() {
      closeDialog();
      router.push({ name: "Dashboard", query: { openStudies: "true" } });
    }

    function viewImportedStudy() {
      if (!importedStudyId.value) {
        closeDialog();
        return;
      }
      closeDialog();
      router.push({ name: "StudyView", params: { id: importedStudyId.value } });
    }

    function editImportedStudy() {
      if (!importedStudyId.value) {
        closeDialog();
        return;
      }
      closeDialog();
      router.push({
        name: "CreateStudy",
        params: { id: importedStudyId.value },
        query: {
          mode: "edit",
          single: "true",
          returnTo: "Dashboard",
          returnId: "",
          returnTab: "edit",
          step: "1",
        },
      });
    }

    // ============ STEP 1 ============
    function validateStudy(opts = { advance: true }) {
      showStudyErrors.value = true;
      const hasMissing = studySchema.value.some((f) => f.required && !studyData.value[f.field]);
      stepErrors.value[1] = hasMissing;
      if (hasMissing) return false;

      showStudyErrors.value = false;
      stepErrors.value[1] = false;

      const typeField = studySchema.value.find((f) => f.field === "type");
      const skipConfig = typeField?.skip || {};
      const selected = studyData.value.type;
      const skips = skipConfig[selected] || [];

      commitStudyDetailsPreservingForms({
        study: studyData.value,
        groups: [],
        visits: [],
        assignments: assignments.value,
        skipSubjectCreationNow: skipSubjectCreationNow.value,
      });

      if (!isEditing.value && opts.advance) {
        if (skips.includes("groups") && skips.includes("visits")) {
          allowInternalNav.value = true;
          router
            .push({ name: "CreateFormScratch", params: { assignments: assignments.value } })
            .finally(() => (allowInternalNav.value = false));
          return false;
        }
      }

      if (opts.advance) step.value = 2;
      return true;
    }

    // ============ STEP 2 ============
    function checkGroups(opts = { advance: true }) {
      if (!groupData.value || groupData.value.length === 0) {
        stepErrors.value[2] = true;
        dialogMessage.value = "Please add at least one group.";
        dialogMode.value = "default";
        showDialog.value = true;
        return false;
      }

      const hasErrors = groupData.value.some((g) =>
        groupSchema.value.some((f) => f.required && !g[f.field])
      );
      stepErrors.value[2] = hasErrors;
      if (hasErrors) return false;

      commitStudyDetailsPreservingForms({
        study: studyData.value,
        groups: groupData.value,
        visits: [],
        assignments: assignments.value,
        skipSubjectCreationNow: skipSubjectCreationNow.value,
      });

      stepErrors.value[2] = false;
      if (opts.advance) step.value = 3;
      return true;
    }

    // ============ STEP 3 ============
    function skipManualEnrollment() {
      subjectData.value = _deepClone(persistedSubjects.value);
      subjectCount.value = subjectData.value.length;
      subjectIdConfig.value.manualIds = subjectData.value.map(subject => subject.id || subject.subject_id);
      skipSubjectCreationNow.value = true;
      checkSubjectsSetup();
    }

    function checkSubjectsSetup(opts = { advance: true, silent: false }) {
      if (skipSubjectCreationNow.value) {
        stepErrors.value[3] = false;

        commitStudyDetailsPreservingForms({
          study: studyData.value,
          groups: groupData.value,
          subjectCount: Number(subjectCount.value || 0),
          assignmentMethod: assignmentMethod.value || "Random",
          subjectIdConfig: _deepClone(subjectIdConfig.value || null),
          subjects: subjectData.value,
          assignments: assignments.value,
          skipSubjectCreationNow: skipSubjectCreationNow.value,
        });

        if (opts.advance) step.value = 5;
        return true;
      }

      if (normalizeSubjectIdConfig(subjectIdConfig.value).mode === "manual") {
        try {
          subjectData.value = reconcileManualSubjects(
            subjectIdConfig.value.manualIds || [], subjectData.value, persistedSubjects.value,
            groupData.value.map(group => group.name || group.label || "Unnamed"), assignmentMethod.value === "Random"
          );
          subjectCount.value = subjectData.value.length;
          commitStudyDetailsPreservingForms({
            subjectCount: subjectCount.value, assignmentMethod: assignmentMethod.value,
            subjectIdConfig: _deepClone(subjectIdConfig.value), subjects: _deepClone(subjectData.value),
            skipSubjectCreationNow: false,
          });
          stepErrors.value[3] = false;
          if (opts.advance) step.value = assignmentMethod.value === "Skip" ? 5 : 4;
          return true;
        } catch (error) {
          stepErrors.value[3] = true;
          if (!opts.silent) {
            dialogMessage.value = error.message;
            dialogMode.value = "default";
            showDialog.value = true;
          }
          return false;
        }
      }

      if (!subjectCount.value || !assignmentMethod.value) {
        // In silent mode (used during edit-hydration), default safely and continue.
        if (opts?.silent) {
          if (!subjectCount.value) subjectCount.value = 1;
          if (!assignmentMethod.value) assignmentMethod.value = "Random";
        } else {
          dialogMessage.value = "Please provide the subject count and the assignment method before continuing.";
          dialogMode.value = "default";
          showDialog.value = true;
          stepErrors.value[3] = true;
          return false;
        }
      }

      const N = Number(subjectCount.value || 0);
      if (!Number.isFinite(N) || N < 0) {
        dialogMessage.value = "Please provide a valid subject count.";
        dialogMode.value = "default";
        showDialog.value = true;
        stepErrors.value[3] = true;
        return false;
      }

      const cfg = normalizeSubjectIdConfig(
        subjectIdConfig.value,
        studyData.value || {},
        headerStudyName.value || "Study"
      );

      subjectIdConfig.value = {
        ...cfg,
        locked: !!isPublishedStudy.value,
      };

      const validationMessage = subjectIdPatternValidationMessage(cfg);

      if (validationMessage) {
        dialogMessage.value = validationMessage;
        dialogMode.value = "default";
        showDialog.value = true;
        stepErrors.value[3] = true;
        return false;
      }

      const groupNames = groupData.value.map((g) => g.name || g.label || "Unnamed");
      const existingSubjects = Array.isArray(subjectData.value)
        ? _deepClone(subjectData.value)
        : [];
      const currentCount = existingSubjects.length;

      if (isEditing.value) {
        // Subject identity becomes immutable as soon as a study (including a
        // draft) has been persisted. Keep each existing ID at its original
        // array position and generate IDs only for appended subjects.
        const nextSubjects = existingSubjects.slice(0, N);
        const existingIds = new Set(
          nextSubjects
            .map((subject) => String(subject?.id || subject?.subject_id || "").trim())
            .filter(Boolean)
        );
        let nextSequenceNumber = getNextSubjectSequenceNumber(
          existingSubjects,
          subjectIdConfig.value
        );

        for (let idx = currentCount; idx < N; idx += 1) {
          const nextId = buildUniqueSubjectIdForCurrentStudy(
            subjectIdConfig.value,
            nextSequenceNumber,
            existingIds
          );
          nextSequenceNumber += 1;
          existingIds.add(nextId);
          nextSubjects.push({
            id: nextId,
            group:
              assignmentMethod.value === "Random" && groupNames.length > 0
                ? groupNames[Math.floor(Math.random() * groupNames.length)]
                : "",
          });
        }

        subjectData.value = nextSubjects;
      } else {
        // Preserve the existing create-flow behavior: before the study has ever
        // been saved, changing the ID configuration regenerates the full list.
        const regeneratedIds = new Set();
        const nextSubjects = [];

        for (let idx = 0; idx < N; idx += 1) {
          const existing = existingSubjects[idx] || {};
          const sequenceNumber = getSubjectSequenceNumberForIndex(idx);
          const nextId = buildUniqueSubjectIdForCurrentStudy(
            subjectIdConfig.value,
            sequenceNumber,
            regeneratedIds
          );

          regeneratedIds.add(nextId);
          nextSubjects.push({
            ...existing,
            id: nextId,
            group:
              existing?.group ||
              (assignmentMethod.value === "Random" && groupNames.length > 0
                ? groupNames[Math.floor(Math.random() * groupNames.length)]
                : ""),
          });
        }

        subjectData.value = nextSubjects;
      }

      commitStudyDetailsPreservingForms({
        study: studyData.value,
        groups: groupData.value,
        subjectCount: N,
        assignmentMethod: assignmentMethod.value || "Random",
        subjectIdConfig: _deepClone(subjectIdConfig.value || null),
        subjects: _deepClone(subjectData.value),
        assignments: assignments.value,
        skipSubjectCreationNow: skipSubjectCreationNow.value,
      });

      stepErrors.value[3] = false;

      if (opts.advance) {
        step.value = assignmentMethod.value === "Skip" ? 5 : 4;
      }

      return true;
    }

    // ============ STEP 4 ============
    function checkSubjectsAssigned(opts = { advance: true }) {
      if (subjectData.value.some((s) => !s.group)) {
        dialogMessage.value = "All subjects must have a group.";
        dialogMode.value = "default";
        showDialog.value = true;
        stepErrors.value[4] = true;
        return false;
      }
      stepErrors.value[4] = false;
      if (opts.advance) step.value = 5;
      return true;
    }

    // ============ STEP 5 ============
    function checkVisits() {
      if (!visitData.value || visitData.value.length === 0) {
        stepErrors.value[5] = true;
        dialogMessage.value = "Please add at least one visit.";
        dialogMode.value = "default";
        showDialog.value = true;
        return false;
      }

      const hasErrors = visitData.value.some((v) =>
        visitSchema.value.some((f) => f.required && !v[f.field])
      );
      stepErrors.value[5] = hasErrors;
      if (hasErrors) return false;

      commitStudyDetailsPreservingForms({
        study: studyData.value,
        groups: groupData.value,
        subjectCount: subjectCount.value,
        assignmentMethod: assignmentMethod.value,
        subjects: subjectData.value,
        visits: visitData.value,
        assignments: assignments.value,
        skipSubjectCreationNow: skipSubjectCreationNow.value,
      });

      stepErrors.value[5] = false;
      return true;
    }

    function goBackFromVisits() {
      if (skipSubjectCreationNow.value) step.value = 3;
      else step.value = assignmentMethod.value === "Skip" ? 3 : 4;
    }

    async function goToFinish() {
      const ok = checkVisits();
      if (!ok) return;
      openFinishRoute();
    }

    // ============ STEPPER LOGIC ============
    const steps = computed(() => {
      const disableAssign = skipSubjectCreationNow.value || assignmentMethod.value === "Skip";
      const rawSteps = [
        { id: 1, label: stepLabelById[1], disabled: false },
        { id: 2, label: stepLabelById[2], disabled: false },
        { id: 3, label: stepLabelById[3], disabled: false },
        { id: 4, label: stepLabelById[4], disabled: disableAssign },
        { id: 5, label: stepLabelById[5], disabled: false },
        { id: 6, label: stepLabelById[6], disabled: false },
      ];
      return rawSteps.map((raw) => {
        const disabled = raw.disabled;
        const hasError = !!stepErrors.value[raw.id];
        const isCompleted = raw.id < step.value && !hasError && !disabled;
        return { ...raw, disabled, hasError, isCompleted };
      });
    });

    // FIX: in edit mode, stepper navigation previously bypassed step-3 regeneration logic,
    // so changing subjectCount/assignmentMethod left stale subjectData visible in step 4.
    // Before entering step 4/5/6, re-run subject setup (without advancing steps) to sync subjectData.
    function onStepClick(targetStep) {
      const disableAssign = skipSubjectCreationNow.value || assignmentMethod.value === "Skip";
      if (targetStep === step.value) return;
      if (targetStep === 4 && disableAssign) return;

      goToStep(targetStep);
    }

    function goToStep(targetStep) {
      if (targetStep < 1 || targetStep > 6) return;

      const disableAssign = skipSubjectCreationNow.value || assignmentMethod.value === "Skip";
      if (targetStep === 4 && disableAssign) return;

      if (targetStep < step.value) {
        step.value = targetStep;
        return;
      }

      const validators = {
        1: () => validateStudy({ advance: true }),
        2: () => checkGroups({ advance: true }),
        3: () => checkSubjectsSetup({ advance: true }),
        4: () => checkSubjectsAssigned({ advance: true }),
        5: () => checkVisits(),
      };

      const maxLogicalStep = targetStep === 6 ? 5 : Math.min(targetStep - 1, 5);
      if (maxLogicalStep <= 0) {
        step.value = 1;
        return;
      }

      const sequence = [1, 2, 3, 4, 5].filter(
        (id) => id <= maxLogicalStep && !(id === 4 && disableAssign)
      );

      for (const id of sequence) {
        if (step.value !== id) step.value = id;
        const fn = validators[id];
        if (!fn) continue;
        const ok = fn();
        if (!ok) return;
      }

      if (targetStep === 6) {
        // Finish in stepper should behave exactly like the Finish button:
        // validate required steps, then go to ScratchForm component.
        openFinishRoute();
        return;
      }

      if (targetStep <= 5) step.value = targetStep;
    }

    function normalizeTargetStep(n) {
      let t = Number(n);
      if (!Number.isFinite(t)) return 1;
      if (t < 1) t = 1;
      if (t > 6) t = 6;

      const disableAssign = skipSubjectCreationNow.value || assignmentMethod.value === "Skip";
      if (t === 4 && disableAssign) t = 5;

      return t;
    }

    function applyInitialStepFromQuery() {
      if (!isEditing.value) return;
      const qs = route.query.step;
      if (qs == null) return;

      const target = normalizeTargetStep(qs);
      if (target === 6) {
        openFinishRoute();
        return;
      }
      step.value = target;
    }

    watch(
      () => route.query.step,
      () => applyInitialStepFromQuery()
    );

    async function ensureStoreDetailsLoadedForEdit() {
      if (!isEditing.value) return;

      const details = store.state.studyDetails;
      const hasSomeData = !!(details && details.study && Object.keys(details.study).length);
      if (hasSomeData) return;

      if (!token.value) return;

      try {
        await hydrateStoreFromStudyId(editId.value);
      } catch (err) {
        console.warn("Failed to fetch study for edit URL load:", err?.response?.data || err?.message || err);
      }
    }

    watch(
      () => editId.value,
      async (newVal, oldVal) => {
        if (newVal === oldVal) return;

        const returningFromScratch = isReturningFromScratch();

        if (!newVal) {
          if (!returningFromScratch) {
            store.commit("resetStudyDetails");

            const base = {};
            (studySchema.value || []).forEach((f) => (base[f.field] = ""));
            studyData.value = base;
            groupData.value = [];
            subjectData.value = [];
            visitData.value = [];
            subjectCount.value = 1;
            assignmentMethod.value = "Random";
            assignments.value = [];
            subjectIdConfig.value = createDefaultSubjectIdConfig(base, "Study");
            skipSubjectCreationNow.value = false;
            step.value = 1;
            draftStudyId.value = null;

            markSavedSnapshot();
          } else {
            populateRefsFromStore();
            applyInitialStepFromQuery();
            // IMPORTANT: do NOT mark saved here
          }
          return;
        }

        await ensureStoreDetailsLoadedForEdit();
        populateRefsFromStore();
        applyInitialStepFromQuery();

        // IMPORTANT: only mark saved on real edit-load, not on return from ScratchForm
        if (!returningFromScratch) {
          markSavedSnapshot();
        }
      }
    );

    // beforeunload (close tab/window)
    function beforeUnloadHandler(e) {
      if (!guardEnabled.value) return;
      computeDirty();
      if (!isDirty.value && !globalDirty.value) return;
      e.preventDefault();
      e.returnValue = "";
    }

    // route leave guard (covers logout/logo navigation/etc.)
    onBeforeRouteLeave((to, from, next) => {
      if (!guardEnabled.value) return next();
      if (allowInternalNav.value) return next();

      computeDirty();
      if (!isDirty.value && !globalDirty.value) return next();

      openUnsavedDialog();
      next(false);
    });

    async function onUnsavedSaveAndExit() {
      if (isPublishedStudy.value) {
        closeDialog();
        requestSaveAndContinue();
        return;
      }
      if (unsavedBusy.value) return;
      unsavedBusy.value = true;
      try {
        const saved = await saveNow({ action: "exit" });
        if (!saved) return;

        showDialog.value = false;
        dialogMode.value = "default";
        dialogMessage.value = "";

        allowInternalNav.value = true;
        router.push("/dashboard").finally(() => {
          allowInternalNav.value = false;
        });
      } finally {
        unsavedBusy.value = false;
      }
    }

    function onUnsavedKeepEditing() {
      closeDialog();
    }

    function onUnsavedExitWithoutSaving() {
      closeDialog();
      allowInternalNav.value = true;
      router.push("/dashboard").finally(() => {
        allowInternalNav.value = false;
      });
    }

    // dirty tracking
    watch(
      [
        studyData,
        groupData,
        subjectData,
        visitData,
        subjectCount,
        assignmentMethod,
        assignments,
        skipSubjectCreationNow,
        draftStudyId,
      ],
      () => {
        if (!guardEnabled.value) return;
        computeDirty();
      },
      { deep: true }
    );

    onMounted(async () => {
      window.addEventListener(STUDY_BEFORE_IDLE_EVENT, saveDraftBeforeIdle);
      const returningFromScratch = isReturningFromScratch();

      if (!editId.value && !returningFromScratch) {
        store.commit("resetStudyDetails");
        localStorage.removeItem("scratchForms");
      }

      await loadYaml("/study_schema.yaml", studySchema);
      await loadYaml("/group_schema.yaml", groupSchema);
      await loadYaml("/visit_schema.yaml", visitSchema);

      await ensureStoreDetailsLoadedForEdit();

      const hasStoreDetails =
        !!store.state.studyDetails &&
        (
          (store.state.studyDetails.study && Object.keys(store.state.studyDetails.study).length) ||
          (Array.isArray(store.state.studyDetails.groups) && store.state.studyDetails.groups.length) ||
          (Array.isArray(store.state.studyDetails.visits) && store.state.studyDetails.visits.length) ||
          store.state.studyDetails.subjectCount != null
        );

      if (isEditing.value || hasStoreDetails) {
        populateRefsFromStore();
        applyInitialStepFromQuery();
      } else {
        const base = {};
        (studySchema.value || []).forEach((f) => (base[f.field] = ""));
        studyData.value = base;
        subjectIdConfig.value = createDefaultSubjectIdConfig(base, "Study");
        applyInitialStepFromQuery();
      }

      // IMPORTANT FIX:
      // Do not clear dirty/snapshot when simply returning from ScratchForm.
      if (!returningFromScratch) {
        markSavedSnapshot();
      }

      window.addEventListener("beforeunload", beforeUnloadHandler);
    });

    onBeforeUnmount(() => {
      window.removeEventListener(STUDY_BEFORE_IDLE_EVENT, saveDraftBeforeIdle);
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    });

    return {
      step,
      studyData,
      groupData,
      subjectData,
      visitData,
      subjectCount,
      subjectIdConfig,
      persistedSubjects,
      skipManualEnrollment,
      hasExistingSubjects,
      isPublishedStudy,
      assignmentMethod,
      assignments,
      studySchema,
      groupSchema,
      visitSchema,
      showDialog,
      dialogMessage,
      dialogMode,
      unsavedBusy,
      lastSavedAt,
      saveError,
      saveDirty,
      currentStudyStatus,
      canAutoSaveDraft,
      skipSubjectCreationNow,

      steps,
      isEditing,
      headerStudyName,
      panelTitle,

      stepLabelById,
      nextLabelFromStep3,
      prevLabelForVisits,

      saveExitLabel,

      fieldError,
      validateStudy,
      checkGroups,
      checkSubjectsSetup,
      checkSubjectsAssigned,
      checkVisits,

      goBackFromVisits,
      onStepClick,
      backFromStep1() {
        if (isEditing.value) router.push(resolveReturnRoute());
        else {
          allowInternalNav.value = true;
          router.push("/dashboard").finally(() => (allowInternalNav.value = false));
        }
      },
      saveThisStepAndBackToStudy,
      goToFinish,

      templateFileInput,
      importingTemplate,
      templateFileName,
      templateImportError,
      triggerTemplatePick,
      openStudyDataImport,
      onTemplatePicked,

      closeDialog,
      goDashboardAfterImport,
      viewImportedStudy,
      editImportedStudy,

      onUnsavedKeepEditing,
      onUnsavedExitWithoutSaving,
      onUnsavedSaveAndExit,
      requestSaveAndContinue,
      confirmPublishedSaveAndContinue,
      saveDraftBeforeIdle,
      onSubjectSetupChanged,
    };
  },
};
</script>
<style scoped>
.study-creation-container {
  max-width: none;
  margin: 0 auto;
}

/* Step 1 header row (title + import action) */
.step1-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.step-title {
  margin: 0;
}
.import-template-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  min-width: 240px;
}
.import-action-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.hidden-file {
  display: none;
}
.import-file-name {
  max-width: 320px;
  font-size: 12px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.import-error {
  margin: 0;
  color: #dc2626;
  font-size: 12px;
}

@media (max-width: 640px) {
  .step1-header {
    flex-direction: column;
    align-items: stretch;
  }
  .import-template-actions {
    align-items: flex-start;
    min-width: 0;
  }
  .import-action-buttons {
    justify-content: flex-start;
  }
  .import-file-name {
    max-width: 100%;
  }
}

/* STEPPER */
.stepper {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-top: 20px;
}
.stepper::before {
  content: "";
  position: absolute;
  top: 16px;
  left: 0;
  right: 0;
  height: 2px;
  background: #e5e7eb;
  z-index: 0;
}
.stepper-item {
  position: relative;
  flex: 1;
  text-align: center;
}
.stepper-circle {
  position: relative;
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 2px solid #d1d5db;
  background: #f9fafb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.15s ease;
}
.stepper-circle.is-active {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1d4ed8;
}
.stepper-circle.is-completed {
  border-color: #16a34a;
  background: #dcfce7;
  color: #166534;
}
.stepper-circle.is-error {
  border-color: #dc2626;
  background: #fee2e2;
  color: #b91c1c;
}
.stepper-circle.is-disabled {
  cursor: default;
  opacity: 0.5;
}
.stepper-circle:disabled {
  cursor: default;
}
.stepper-label {
  font-size: 12px;
  color: #4b5563;
}
.stepper-connector {
  position: absolute;
  top: 16px;
  left: 50%;
  width: 100%;
  height: 4px;
  transform: translateY(-50%);
  background: transparent;
  border-radius: 999px;
  z-index: 1;
}
.stepper-connector.connector-completed {
  background: #16a34a;
}
@media (max-width: 640px) {
  .stepper-label {
    font-size: 11px;
  }
  .stepper-circle {
    width: 28px;
    height: 28px;
    font-size: 13px;
  }
}

/* FORMS */
.new-study-form {
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-top: 20px;
}
.schema-field-row {
  margin-bottom: 1rem;
}
.form-actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.btn-option {
  padding: 10px 20px;
  background: #eee;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
}

/* Step 3 toggle row */
.step-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 1rem;
}
.skip-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #111827;
}
.skip-label {
  white-space: nowrap;
  color: #111827;
  font-weight: 400;
  font-size: 14px;
}
.toggle-button {
  position: relative;
  width: 42px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: #e5e7eb;
  padding: 0;
  cursor: pointer;
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.published-step-note {
  margin: 0 0 14px;
  padding: 10px 12px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 13px;
  font-weight: 600;
}
.toggle-button .toggle-knob {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease;
}
.toggle-button.toggle-on {
  background: #3b82f6;
  border-color: #2563eb;
}
.toggle-button.toggle-on .toggle-knob {
  transform: translateX(20px);
}

/* Dialog */
.dialog-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.dialog-box {
  background: white;
  padding: 20px 30px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  text-align: center;
  max-height: 80vh;
  overflow-y: auto;
  width: min(720px, 92vw);
}
.dialog-actions {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
@media (max-width: 760px) {
  .dialog-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
